// packages/web/src/components/CartDrawer.tsx
import React from 'react'
import { X, Trash2, ShoppingBag, ArrowRight, ShieldCheck } from 'lucide-react'
import { ProductImage } from './ProductImage'
import { useCart } from '../context/CartContext'
import { useSiteConfig } from '../context/SiteConfigContext'
import { isGstEnabled } from '../lib/companyGst'

interface CartDrawerProps {
  onCheckout: () => void
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ onCheckout }) => {
  const {
    items,
    removeItem,
    updateQuantity,
    isCartOpen,
    setIsCartOpen,
    subtotal,
    taxCgst,
    taxSgst,
    shippingAmount,
    freeDeliveryMin,
    total,
  } = useCart()
  const { config } = useSiteConfig()
  const gstOn = isGstEnabled(config)

  if (!isCartOpen) return null

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount)

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        onClick={() => setIsCartOpen(false)}
        className="absolute inset-0 bg-[var(--bg-main)]/80 backdrop-blur-sm transition-opacity"
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-[var(--bg-surface-2)] border-l border-[var(--gold-dark)]/20 shadow-2xl flex flex-col justify-between">
          {/* Header */}
          <div className="p-6 border-b border-[var(--gold-dark)]/15 flex items-center justify-between bg-[var(--bg-surface-2)]">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[var(--gold-dark)]" />
              <h2 className="text-lg font-serif font-bold text-[var(--text-primary)]">Shopping Bag</h2>
              <span className="bg-[var(--gold-dark)]/20 text-[var(--gold-dark)] text-xs font-bold px-2 py-0.5 rounded-full">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            <button
              onClick={() => setIsCartOpen(false)}
              className="p-1.5 text-[var(--text-primary)]/60 hover:text-[var(--text-primary)] rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cart Items List */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {items.length === 0 ? (
              <div className="empty-state py-12">
                <ShoppingBag className="w-12 h-12 text-[var(--gold-dark)]/40" />
                <h3 className="font-serif text-lg text-[var(--text-primary)]">Your bag is empty</h3>
                <p className="text-xs text-[var(--text-primary)]/50 max-w-xs">
                  Discover our timeless collections of handcrafted gold, silver and luxury watches.
                </p>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="btn btn-primary btn-sm mt-4"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={`${item.comboOfferId || item.productId}-${item.variantId || 'default'}`}
                  className="glass-card p-3.5 flex gap-3.5 items-center relative group"
                >
                  {/* Thumbnail */}
                  <ProductImage
                    src={item.productImage ?? null}
                    alt={item.productName}
                    className="w-16 h-16 rounded-lg shrink-0"
                    imgClassName="w-16 h-16 object-cover rounded-lg border border-[var(--border)]"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xs font-serif font-bold text-[var(--text-primary)] truncate">
                      {item.productName}
                    </h4>
                    {item.variantDescription && (
                      <p className="text-[10px] text-[var(--gold-dark)] mt-0.5 truncate">
                        {item.variantDescription}
                      </p>
                    )}
                    <p className="text-xs font-bold text-[var(--gold-dark)] mt-1">
                      {formatCurrency(item.unitPrice)}
                    </p>

                    {/* Quantity controls */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <div className="flex items-center border border-[var(--gold-dark)]/20 rounded bg-[var(--bg-surface-3)] text-xs">
                        <button
                          onClick={() =>
                            updateQuantity(item.productId, item.variantId, item.quantity - 1, item.comboOfferId)
                          }
                          className="px-2 py-0.5 text-[var(--text-primary)] hover:bg-[var(--gold-dark)]/20"
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10)
                            if (Number.isFinite(n) && n >= 1) {
                              updateQuantity(item.productId, item.variantId, n, item.comboOfferId)
                            }
                          }}
                          className="w-14 text-center font-semibold text-[var(--text-primary)] bg-transparent py-0.5 outline-none border-x border-[var(--gold-dark)]/20"
                          aria-label="Quantity"
                        />
                        <button
                          onClick={() =>
                            updateQuantity(item.productId, item.variantId, item.quantity + 1, item.comboOfferId)
                          }
                          className="px-2 py-0.5 text-[var(--text-primary)] hover:bg-[var(--gold-dark)]/20"
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                      <span className="text-[11px] text-[var(--text-primary)]/50 font-mono">
                        = {formatCurrency(item.unitPrice * item.quantity)}
                      </span>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeItem(item.productId, item.variantId, item.comboOfferId)}
                    className="p-1.5 text-red-400/60 hover:text-red-400 transition hover:bg-red-500/10 rounded"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Footer Summary & Checkout */}
          {items.length > 0 && (
            <div className="p-6 border-t border-[var(--gold-dark)]/20 bg-[var(--bg-surface-2)] space-y-4">
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between text-[var(--text-primary)]/70">
                  <span>Items Subtotal:</span>
                  <span className="font-mono">{formatCurrency(subtotal)}</span>
                </div>
                {gstOn && (
                  <>
                    <div className="flex justify-between text-[var(--text-primary)]/50">
                      <span>CGST (3%):</span>
                      <span className="font-mono">{formatCurrency(taxCgst)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--text-primary)]/50">
                      <span>SGST (3%):</span>
                      <span className="font-mono">{formatCurrency(taxSgst)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-[var(--text-primary)]/70">
                  <span>Shipping:</span>
                  <span className={shippingAmount === 0 ? 'text-emerald-400 font-semibold' : 'font-mono'}>
                    {shippingAmount === 0 ? 'FREE' : formatCurrency(shippingAmount)}
                  </span>
                </div>
                {shippingAmount > 0 && (
                  <p className="text-[10px] text-[var(--text-faint)]">
                    Free delivery on orders of ₹{freeDeliveryMin.toLocaleString('en-IN')} and above.
                  </p>
                )}
                <div className="pt-2 border-t border-[var(--gold-dark)]/15 flex justify-between text-base font-serif font-bold text-[var(--gold-dark)]">
                  <span>Total Payable:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              {gstOn && (
                <div className="flex items-center gap-1.5 text-[10px] text-[var(--text-primary)]/50 justify-center">
                  <ShieldCheck className="w-3.5 h-3.5 text-[var(--gold-dark)]" />
                  <span>GST tax invoice generated upon order confirmation.</span>
                </div>
              )}

              <button
                onClick={() => {
                  setIsCartOpen(false)
                  onCheckout()
                }}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[var(--gold-dark)] to-[var(--gold-dark)] text-[var(--bg-surface-2)] font-bold text-sm hover:brightness-110 transition shadow-lg flex items-center justify-center gap-2"
              >
                <span>Proceed to Checkout</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
